// One mapping from the offboarding engine's status + outcome to the Soccer
// Post UI's five states, shared by every Offboarding screen.
//   ful  complete        par  running / scheduled   hold  needs a person
//   can  failed          none no account found      skip  skipped / cancelled

const OUTCOME = {
  no_account_found: ['none', 'No account found'],
  scheduled: ['par', 'Scheduled'],
  completed: ['ful', 'Completed'],
  completed_manually: ['ful', 'Completed manually'],
  skipped: ['skip', 'Skipped'],
  cancelled: ['skip', 'Cancelled'],
};

const STATUS = {
  success: ['ful', 'Complete'],
  failure: ['can', 'Failed'],
  in_progress: ['par', 'In progress'],
  requires_feedback: ['hold', 'Needs attention'],
};

// Status decides the tone when it signals trouble or work in flight;
// the outcome only refines a finished (success) state's label.
export function stateOf(status, outcome) {
  if (status === 'success' && outcome && OUTCOME[outcome]) return OUTCOME[outcome];
  if (status === 'in_progress' && outcome === 'scheduled') return OUTCOME.scheduled;
  if (status === 'in_progress' && (outcome === 'queued_for_tech' || outcome === 'sending')) return ['par', 'Queued for tech@ email'];
  return STATUS[status] || ['neu', status || 'Unknown'];
}

export const isScheduled = (run) => !!run.scheduled_for && new Date(run.scheduled_for) > new Date();

// Run-level state. A finished run where every system found nothing reads
// "No account found" rather than a misleading "Complete".
export function runState(run) {
  if (isScheduled(run)) return ['par', 'Scheduled'];
  const systems = run.systems || [];
  if (run.status === 'success' && systems.length && systems.every((s) => s.outcome === 'no_account_found')) {
    return OUTCOME.no_account_found;
  }
  return stateOf(run.status);
}

// Pill tones: 'none' and 'skip' both render as the neutral pill.
export const pillTone = (tone) => (tone === 'none' || tone === 'skip' ? 'neu' : tone);

const SYSTEM_LABELS = {
  workspace: 'Google Workspace',
  shopify: 'Shopify',
  shopify_staff: 'Shopify staff',
  team_portal: 'Team Portal',
  operations_hub: 'Operations Hub',
  operations_hub_pin: 'Ops Hub PIN',
  gorgias: 'Gorgias',
  lightspeed: 'Lightspeed',
  zoom: 'Zoom',
  unifi: 'UniFi',
  capsule: 'Capsule',
  klaviyo: 'Klaviyo',
  finance_access: 'Finance access',
};

export const systemLabel = (system) =>
  SYSTEM_LABELS[system] || system.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// An account's display state: its own status, refined by the outcome all of
// its actions share (same rule as the backend's history summaries).
export function accountState(account) {
  const outcomes = (account.actions || []).map((a) => a.outcome);
  const uniform = outcomes.length > 0 && outcomes.every((o) => o === outcomes[0]) ? outcomes[0] : null;
  return stateOf(account.status, uniform);
}

export function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Step markers offboard-phase1.sh writes to its log, in the order shown.
const PHASE1_STEPS = [
  [/deprovisioning:/, () => 'Security updated'],
  [/removing from (\d+) group/, (m) => `Removed from ${m[1]} group${m[1] === '1' ? '' : 's'}`],
  [/renaming primary address to/, () => 'Renamed'],
  [/creating continuity group/, () => 'Continuity group created'],
  [/^\S+ \S+\s+suspending\s*$/m, () => 'Suspended'],
  [/starting background GYB backup/, () => 'Backup started'],
];

export function phase1Steps(log) {
  return PHASE1_STEPS.map(([re, text]) => {
    const m = (log || '').match(re);
    return m ? text(m) : null;
  }).filter(Boolean);
}

// Once Phase 1 has renamed an account, re-running it can't find the
// original address and would wrongly report success (the backend refuses
// too; this just says why before anyone clicks).
export function retryBlockedReason(action) {
  if (action.action_key === 'suspend_and_configure' && (action.detail || '').includes('renaming primary address to')) {
    return "Retry isn't available: the account was already renamed, so running Phase 1 again can't find it. "
      + 'Finish the failed step by hand in Google Admin, then mark it done.';
  }
  return null;
}

export const splitRecipients = (forwardTo) => (forwardTo || '').split(/[;,]/).map((s) => s.trim()).filter(Boolean);

const firstLine = (text, max = 140) => {
  const line = (text || '').split('\n').map((l) => l.trim()).find(Boolean) || '';
  return line.length > max ? `${line.slice(0, max - 1)}…` : line;
};

function actionSummary(account, action) {
  if (action.action_key === 'suspend_and_configure' && (action.detail || '').includes('PHASE 1')) {
    return phase1Steps(action.detail).join(', ').toLowerCase() || 'done';
  }
  if (action.action_key === 'archive_mail_and_drive') return 'archive finished';
  return firstLine(action.detail) || action.label;
}

// The Activity card: when the run started, each step's result, and what
// people did afterwards (retries, mark done, forwarding changes). Systems
// with no account collapse into one line. Newest first.
// tone: 'ok' green dot, 'hot' orange (waiting on you), '' grey.
export function buildActivity(run, nameOf = (x) => x) {
  const items = [];
  const events = run.events || [];
  const systemsWithEvents = new Set(events.map((e) => e.system).filter(Boolean));
  const noAccount = [];

  items.push({ at: run.created_at, tone: '', text: run.initiated_by ? `Run started by ${nameOf(run.initiated_by)}` : 'Run started' });

  (run.accounts || []).forEach((account) => {
    const name = systemLabel(account.system);
    account.actions.forEach((a) => {
      if (a.outcome === 'no_account_found') {
        if (!noAccount.includes(name)) noAccount.push(name);
        return;
      }
      if (a.status === 'in_progress' || a.outcome === 'scheduled') return;
      if (a.outcome === 'completed_manually' && systemsWithEvents.has(account.system)) return;
      const label = a.action_key === 'archive_mail_and_drive' ? `${name} archive` : name;
      if (a.status === 'requires_feedback') {
        const n = (a.match_candidates || []).length;
        items.push({ at: a.updated_at, tone: 'hot', text: `${label}: ${n ? `${n} possible match${n === 1 ? '' : 'es'}, waiting for a choice` : 'manual step, waiting for you'}` });
      } else if (a.status === 'failure') {
        items.push({ at: a.updated_at, tone: '', text: `${label}: failed. ${firstLine(a.detail)}` });
      } else if (a.outcome === 'cancelled' || a.outcome === 'skipped') {
        items.push({ at: a.updated_at, tone: '', text: `${label}: ${a.outcome}` });
      } else {
        items.push({ at: a.updated_at, tone: 'ok', text: `${label}: ${actionSummary(account, a)}` });
      }
    });
  });

  if (noAccount.length) items.push({ at: run.created_at, tone: '', text: `No account found in ${noAccount.join(', ')}` });

  events.forEach((e) => items.push({
    at: e.created_at,
    tone: e.tone === 'ok' ? 'ok' : '',
    text: `${e.system ? `${systemLabel(e.system)}: ` : ''}${e.text}`,
  }));

  return items
    .map((item, i) => ({ ...item, i }))
    .sort((a, b) => (new Date(b.at) - new Date(a.at)) || (b.i - a.i));
}

// CSV-only pseudo-runs carry string ids ('csv-<email>') and must never be
// sent to a /full-run/{id} API route.
export const isRealRun = (run) => typeof run.id === 'number';
export const runPath = (run) => `/offboarding/runs/${encodeURIComponent(String(run.id))}`;
